import { messages, type TranslationKey } from "./resources";
import { type AppLocale, type MessageValues, SUPPORTED_LOCALES } from "./types";

const DEFAULT_LOCALE: AppLocale = "en";
const LOCALE_STORAGE_KEY = "tickbird.locale";
const listeners = new Set<() => void>();

function isSupportedLocale(value: string | null | undefined): value is AppLocale {
  return SUPPORTED_LOCALES.includes(value as AppLocale);
}

function localeFromLanguageTag(language: string): AppLocale | null {
  const normalized = language.trim().toLowerCase();
  if (normalized.startsWith("zh")) {
    return "zh-Hans";
  }
  if (normalized.startsWith("en")) {
    return "en";
  }
  return null;
}

/**
 * 语言的持久化行为。嵌入端把两项都关掉：外观完全受控于宿主，
 * iframe 里残留的 `tickbird.locale` 会覆盖宿主推送，并跨宿主、跨用户残留
 * （docs/embedded-workbench-design.md §5.5.3、AR-30）。
 */
type LocaleRuntimeOptions = { persist: boolean; syncAcrossTabs: boolean };

let localeRuntime: LocaleRuntimeOptions = { persist: true, syncAcrossTabs: true };

function detectInitialLocale(): AppLocale {
  if (typeof window === "undefined") {
    return DEFAULT_LOCALE;
  }

  if (localeRuntime.persist) {
    try {
      const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
      if (isSupportedLocale(stored)) {
        return stored;
      }
    } catch {
      // Storage can be unavailable in private browsing; browser preferences still work.
    }
  }

  for (const language of window.navigator.languages ?? [window.navigator.language]) {
    const detected = localeFromLanguageTag(language);
    if (detected) {
      return detected;
    }
  }

  return DEFAULT_LOCALE;
}

let currentLocale = detectInitialLocale();

/**
 * 由 `main.tsx` 在渲染 React 之前按 surface 调用。
 * 关闭持久化时会重新按浏览器语言判定一次，丢掉模块初始化阶段从 localStorage 读到的值。
 */
export function configureLocaleRuntime(options: LocaleRuntimeOptions): void {
  localeRuntime = options;
  if (!options.persist) {
    const detected = detectInitialLocale();
    if (detected !== currentLocale) {
      currentLocale = detected;
      applyLocaleToDocument(currentLocale);
      listeners.forEach((listener) => {
        listener();
      });
    }
  }
}

export function getLocale(): AppLocale {
  return currentLocale;
}

export function getIntlLocale(locale: AppLocale = currentLocale): string {
  return locale === "zh-Hans" ? "zh-CN" : "en-US";
}

export function getAcceptLanguage(locale: AppLocale = currentLocale): string {
  return locale;
}

function formatMessage(template: string, values?: MessageValues): string {
  if (!values) {
    return template;
  }

  return template.replace(/\{([A-Za-z0-9_]+)\}/g, (placeholder, name: string) => {
    const value = values[name];
    return value === null || value === undefined ? placeholder : String(value);
  });
}

export function translate(key: TranslationKey, values?: MessageValues, locale: AppLocale = currentLocale): string {
  return formatMessage(messages[locale][key] ?? messages[DEFAULT_LOCALE][key], values);
}

/** Request interceptors and non-React presentation helpers use the current locale directly. */
export const t = translate;

function applyLocaleToDocument(locale: AppLocale) {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.lang = locale;
  document.title = translate("app.title", undefined, locale);
  document.querySelector('meta[name="description"]')?.setAttribute("content", translate("app.description", undefined, locale));
  document.querySelector('link[rel="manifest"]')?.setAttribute("href", `/${locale}/site.webmanifest`);
}

export function setLocale(locale: AppLocale) {
  if (locale === currentLocale) {
    return;
  }

  currentLocale = locale;
  if (localeRuntime.persist) {
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // The in-memory preference still applies when storage is unavailable.
    }
  }
  applyLocaleToDocument(locale);
  listeners.forEach((listener) => {
    listener();
  });
}

export function subscribeToLocale(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

if (typeof window !== "undefined") {
  applyLocaleToDocument(currentLocale);
  window.addEventListener("storage", (event) => {
    // 嵌入端不跨标签同步：宿主是唯一语言来源，别的标签页改了不该影响 iframe。
    if (!localeRuntime.syncAcrossTabs || event.key !== LOCALE_STORAGE_KEY || !isSupportedLocale(event.newValue) || event.newValue === currentLocale) {
      return;
    }
    currentLocale = event.newValue;
    applyLocaleToDocument(currentLocale);
    listeners.forEach((listener) => {
      listener();
    });
  });
}

export type { TranslationKey } from "./resources";
export type { AppLocale, MessageValues } from "./types";
export { SUPPORTED_LOCALES } from "./types";
